"use client";

import type { ComponentProps } from "react";
import DesignSurface from "./DesignSurface";
import MountainSelectLayer from "./MountainSelectLayer";
import CorridorBasemapLayer from "@/components/map/CorridorBasemapLayer";
import TransportMapPanelLayer from "@/components/transport/TransportMapPanelLayer";
import MobileNavSafeArea from "./MobileNavSafeArea";
import ExportUrlSync from "./ExportUrlSync";
import ExportLegacyLinkBridge from "./ExportLegacyLinkBridge";
import ServerLayerParserFirst from "./ServerLayerParserFirst";
import ExportContentBody from "./ExportContentBody";

export interface ExportShellProps {
  slug: string; h1: string; tagline: string; dc: string; served: string[];
  /** SNOWCLUB-STAGING-PAGE-LATENCY-001: render the design on the server and hydrate it; the head block below is then the design's own h1. */
  ssr?: boolean;
  /** UA-derived first-render viewport (390 mobile / 1440 otherwise) so server HTML and hydration agree. */
  vw?: number;
  /** The mountain's supplier directory, injected into the design on both sides (see DesignSurface). */
  suppliers?: { supplierStatus: string; suppliers: unknown[] } | null;
  /** Route content the design does not carry, rendered inside the design surface (no separate layer). */
  routeContent?: React.ReactNode;
}

// Server lookup and state construction remain in page.tsx. This synchronous tree keeps delayed
// Flight records from suspending after React has already claimed <main> or one of its wrappers.
// The complete source markup is still rendered on the server, without a hidden fallback.
export default function ExportMain({ shell, body, attributes = {} }: {
  shell: ExportShellProps;
  body: ComponentProps<typeof ExportContentBody>;
  attributes?: Record<string, string>;
}) {
  // SNOWCLUB-STAGING-PAGE-LATENCY-001: the body is no longer a sibling fallback layer. Painted route content (blocks the
  // design lacks) renders INSIDE the design surface; a plain snapshot body on an ssr route is omitted — the server-rendered
  // design is the document.
  const hasRouteContent = !!body.routeContent || body.paint;
  return <main {...attributes}><ExportShellView {...shell} routeContent={shell.ssr && !hasRouteContent ? null : <ExportContentBody {...body} />} /></main>;
}

export function ExportShellView({ slug, h1, tagline, dc, served, ssr = false, vw = 1440, suppliers = null, routeContent = null }: ExportShellProps) {
  return (
    // E19 — MEASURED ON PROD: body carries the browser default 8px margin on export routes
    // (they bypass the app shell that resets it), so a 100vw x 100vh relative wrapper started at
    // x=8,y=8 and ran to right=1288 against an innerWidth of 1280 — a white band down the left and
    // across the top, content bleeding off the right and bottom. Exactly what the founder saw.
    // position:fixed inset:0 is immune to the ancestor margin; the style tag zeroes it anyway so
    // the document cannot scroll a phantom 8px.
    <>
      {/* ── WO-FOUNDER-WALK-UNBLOCK-001 · T4 — THE FULL-PAGE SPLASH, AND WHY IT IS GONE ─────────────
          Founder: "what is this screen load picture with a comment every time a screen switches to a
          different URL?" THIS IS IT, and it is not a loading screen — it is this panel, found by
          reading the SERVED HTML rather than by guessing: a `position:absolute; inset:0` white sheet
          holding a 560x315 gondola photo, the page H1 and the tagline, sitting UNDER the iframe
          (`zIndex: 2`). It painted on every page load and stayed until the 1.1MB export finished
          parsing. Nobody noticed because for most of this build's life a mountain switch was a
          pushState — no page load, no splash. §29 (`968a1d6`) correctly made a switch a REAL
          navigation, and that turned an always-present paint layer into a flash on every switch.
          So the WO's hypothesis was right about the trigger and wrong about the cause: §29 revealed
          this, it did not create it. Reverting §29 would have hidden the symptom and reintroduced the
          defect it fixed.

          WHAT CHANGES AND WHAT DOES NOT.
          · The PHOTO is gone. It was `alt=""` — decorative by its own declaration, worth nothing to a
            crawler, and it existed only to be the LCP paint. It is the "picture" in his sentence.
          · The H1 and the tagline STAY IN THE SERVED HTML — first visually clipped (the then-current
            crawlable-layer technique), since P0 2026-08-24 as a PAINTED server-layer block below.
            So `verify_parity`'s H1 and meta legs read what they always read, the link graph is
            untouched, and every SEO leg §29 was protecting still holds.
          · The wrapper keeps a solid white background, so the moment before the export paints is
            plain white — a page loading — instead of a branded interstitial that reads as a product
            screen the customer has to wait through.
          The trade AS FIRST SHIPPED: with JS off these routes rendered CLIPPED text — a 1x1
          clip:rect container, the same technique the crawlable layer used. P0 2026-08-24 (card
          86bbe7fty) ended that class: independent verification failed the release on exactly these
          1x1 crawler-facing containers, and the founder's ruling is structural removal. The heading
          below is now a PAINTED [data-sg-server-layer] block in normal flow, BEFORE the fixed
          sheet — delivered visible, real box, no clip — hidden only after the design proves it
          painted (the mountain roots' own fallback lifecycle, DesignSurface state === "up"), and
          W3's demotion still keeps the rendered document at exactly one <h1>. */}
      {/* CRAWLER-EQUIVALENCE REPAIR (2026-09-12) — `6171e1e` ("remove design text flash") put
          `display:none` straight onto this block so the H1 and tagline stopped flashing before the
          design painted. That hid it in the DELIVERED document too, which is the exact class the P0
          ruling above bans: crawler-equivalence-gate leg A (JS off, no allowlist) measured
          `div[data-sg-server-layer] 0x0/none text=100..245` on 339 of the 400 graded paths, and the
          gate has been the one red on every staging release since (runs 34649956675, 34651702442,
          34714539808). Both requirements hold at once when the hiding is done by the VISITOR's
          browser rather than by the server: the inline script below runs before this block is
          parsed and stamps `data-sg-js` on <html>; the rule beside it hides the block only under that
          stamp. With scripting off — leg A's context, and any crawler that does not execute JS — the
          stamp never lands and the block is delivered visible, real box, no clip, exactly as the
          comment above promises. With scripting on it is `display:none` from the first paint, so no
          text flashes. DesignSurface's failed/threw paths set an INLINE `display:block` on every
          server layer, which outranks this stylesheet rule, so a design that does not mount still
          leaves the customer the plain page. A data attribute rather than a class because the root
          layout owns <html>'s className and React would reconcile a class away; it never renders
          `data-sg-js`. Measured under Playwright before landing: JS off -> block 1424x162 visible,
          JS on -> none 0x0, inline display:block restore -> visible in both. */}
      {/* FOUNDER-WALK-LIVE-RECONCILE-025 · F1 — the same stamp, now emitted by ONE component that also
          covers every OTHER [data-sg-server-layer] a design mounts over (the exemplar branch's body
          blocks render after this shell in the same <main>, so they are parsed under the stamp too),
          and that knows how to STAND DOWN when no script ever runs. e12bc's mechanism is unchanged:
          visitor-written stamp, stylesheet rule under it, nothing hidden in the delivered bytes. */}
      <ServerLayerParserFirst title={h1} />
      {/* SNOWCLUB-STAGING-PAGE-LATENCY-001: with the design server-rendered, its own hero <h1> is the served heading —
          this fallback head block would be a second <h1> in the delivered document, so it is retired on ssr routes. */}
      {ssr ? null : (
      <div className="sg-export-head" data-sg-server-layer="" style={{ overflowX: "auto", maxWidth: "100%", padding: "24px 16px" }}>
        <h1
          style={{
            margin: 0,
            fontFamily: "Montserrat, system-ui, sans-serif",
            fontWeight: 700,
            fontSize: "clamp(26px, 4vw, 42px)",
            lineHeight: 1.15,
            color: "#101820",
          }}
        >
          {h1}
        </h1>
        {tagline ? (
          <p
            style={{
              margin: 0,
              maxWidth: 620,
              fontFamily: "system-ui, sans-serif",
              fontSize: 16,
              lineHeight: 1.5,
              color: "#5B666E",
            }}
          >
            {tagline}
          </p>
        ) : null}
      </div>
      )}
      {/* QA-BATCH-01 rows 6/9 (2026-08-27): NO LONGER position:fixed. The fixed inset:0 sheet
          (this one plus DesignSurface's, changed in the same pass) capped document scrollHeight at
          the viewport on every design route — the measured root cause of the footer-containment
          defect: the design's module <main> was the page's only scroller and the footer painted
          inside a results pane beside its own filter rail. In normal flow the design extends the
          document, which becomes the one scroller — the approved control page's behaviour. E19's
          8px body-margin bleed (the reason fixed was chosen) does not return: the style tag below
          still zeroes the body margin on these shell-bypassing routes. */}
      <div style={{ position: "relative", width: "100%", minHeight: "100vh", background: "#fff" }}>
      <style>{"body{margin:0}"}</style>
      {/* The founder's design, mounted IN THIS DOCUMENT. Was an <iframe src="/design/sg-beta-v2.01
          .html?dc=…"> until 2026-08-14; see the block above this function for what changed and what
          it costs. Same file, same contract, same layering (fixed, opaque, z-index 2) — no nested
          browsing context, so the design shares this origin's cookies and this page's session. */}
      <DesignSurface dc={dc} ssr={ssr} vw={vw} path={"/" + slug.replace(/_/g, "/")} suppliers={suppliers}>{routeContent}</DesignSurface>
      {/* Site-wide selector mount for the remaining explicit `gf:mountain-select` entry points. The design's
          own mountain panel does not open this modal: its "Show the map" toggle (and `sg:mountain-map`) opens
          the panel's inline map pane instead — hydrated by HomepageSelectorMapLayer, which DesignSurface mounts
          itself so the roots that render DesignSurface directly (page.tsx's mountain landing) carry it too. */}
      <MountainSelectLayer />
      {/* FOUNDER MAP RULING 2026-08-27 — the schedule index mounts a real basemap immediately.
          Mounted beside the DESIGN surface, not gated on the mirror body: the static tile mosaic
          this replaces is injected client-side by DesignSurface from the design template, so it
          never appears in the server-rendered mirror body a `body.includes(...)` test could see.
          The layer is self-limiting — it looks for the mosaic and returns untouched when a page
          does not carry one, so mounting it on every design route costs nothing elsewhere. */}
      {/* This hydrator is only for the schedule index. Mounting it on every design route also found
          the mountain selector's tile mosaic, replaced that different map with the Vancouver–Whistler
          corridor, and left the selector's percentage-positioned resort pins on top of the wrong map. */}
      {slug.endsWith("_bus-schedule") && <CorridorBasemapLayer />}
      {/* ROW 8 (cards 86bb5yjrg/86bb5yk8d, scoped approval 2026-09-07) — the transport MAP panel's
          schematic placeholder becomes the real KeylessMap on operational stop coordinates, and the
          panel opens at rest on desktop through the design's own toggle. Gated to the bus-schedule
          slug: the panel this layer looks for exists only there, and the gate keeps the observer
          off every other design route. */}
      {slug.endsWith("_bus-schedule") && <TransportMapPanelLayer />}
      {/* FOUNDER 2026-09-02 — the document reserves the strip the design's fixed bottom
          navigation occupies, so the footer's legal row and its Privacy / Terms / Contact /
          FAQ links clear it instead of sitting underneath. Measured from the bar itself plus
          env(safe-area-inset-bottom), and removed again when a surface renders no bar. */}
      <MobileNavSafeArea />
      {/* FOUNDER 2026-08-16 — "every page has to have a url": the host half of the URL sync. */}
      <ExportUrlSync servedPath={"/" + slug.replace(/_/g, "/")} />
      {/* WO-SITE-REAL-002 · M4 — renders nothing; keeps the export's own links ON this site. 61 of the
          home's 197 visible controls pointed at www.snowbus.com, 17 of the 20 destinations being paths
          we serve. The served list is passed in rather than inferred so a path we do NOT serve keeps
          pointing at live instead of becoming a local 404.
          IT HAD TO LEARN TO WORK WITHOUT A FRAME TO SURVIVE THIS CHANGE: it used to bail on
          `querySelector("iframe[src*='sg-beta']")`, so un-framing these pages without touching it
          would have silently reinstated all 61 leaks on every one of them. */}
      <ExportLegacyLinkBridge served={served} />
      </div>
    </>
  );
}
